import { useCallback, useMemo } from 'react'
import { AxiosRequestService } from '@/enums/api'
import { DocumentNode } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { print } from 'graphql'
import CONFIG from './config'

export type ApiService = keyof typeof axiosRequest

const {
  PAYMENTS_SERVICE_URL,
  RESIDENT_SERVICE_URL,
  DMS_SERVICE_URL,
  MAINTENANCE_SERVICE_URL,
} = CONFIG

export const createApiInstance = (
  baseURL: string,
  options?: { isGraphQL?: boolean },
): AxiosInstance => {
  let finalBaseURL = baseURL

  // Handle GraphQL endpoint URL construction
  if (options?.isGraphQL) {
    // If URL already contains /graphql, don't modify it
    if (baseURL.includes('/graphql')) {
      finalBaseURL = baseURL
    }
    // If URL ends with /api, just append /graphql
    else if (baseURL.endsWith('/api')) {
      finalBaseURL = `${baseURL}/graphql`
    }
    // Otherwise, append /api/graphql
    else {
      finalBaseURL = `${baseURL}/api/graphql`
    }
  }

  const instance = axios.create({
    baseURL: finalBaseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response
    },
    (error: AxiosError) => {
      const status = error.response?.status
      switch (status) {
        case 401:
          console.error('Unauthorized access - Auth token may be expired')
          break
        case 403:
          console.error('Forbidden access - User does not have permission')
          break
        case 500:
          console.error('Server error occurred')
          break
        default:
          console.error('API request failed:', error.message)
      }

      return Promise.reject(error)
    },
  )

  return instance
}

// Add the services you need to use
export const axiosRequest: Record<AxiosRequestService, AxiosInstance> = {
  [AxiosRequestService.PAYMENTS]: createApiInstance(PAYMENTS_SERVICE_URL),
  [AxiosRequestService.RESIDENT]: createApiInstance(RESIDENT_SERVICE_URL, {
    isGraphQL: true,
  }),
  [AxiosRequestService.RESIDENT_REST]: createApiInstance(RESIDENT_SERVICE_URL),
  [AxiosRequestService.DMS]: createApiInstance(DMS_SERVICE_URL, {
    isGraphQL: true,
  }),
  [AxiosRequestService.MAINTENANCE]: createApiInstance(MAINTENANCE_SERVICE_URL),
}

/**
 * Hook for making authenticated service requests
 * @param serviceName The service to make requests to
 * @returns Methods for making authenticated requests to the specified service
 */
export const useServiceRequest = (service: ApiService) => {
  const { getAccessTokenSilently, isAuthenticated, user } = useAuth0()

  // Kept as a primitive so it can be a stable hook dependency below.
  const brandId = useMemo(() => {
    const dataAccessKey = `${CONFIG.AUTH0_AUDIENCE}/data_access`
    return user?.[dataAccessKey]?.brands?.[0]?.brand_id as string | undefined
  }, [user])

  /**
   * Resolve the auth headers for a request: the Auth0 access token, plus the
   * caller's brand for GraphQL requests.
   */
  const getAuthHeaders = useCallback(
    async (options?: { includeBrand?: boolean }) => {
      const headers: Record<string, string> = {}

      if (!isAuthenticated) return headers

      try {
        const extendedConfig = CONFIG
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: extendedConfig.AUTH0_AUDIENCE,
          },
        })

        if (token) {
          headers.Authorization = `Bearer ${token}`

          if (options?.includeBrand && brandId) {
            headers['x-pure-brand-id'] = brandId
          }
        }
      } catch (error) {
        console.error('Error getting token for API request:', error)
      }

      return headers
    },
    [getAccessTokenSilently, isAuthenticated, brandId],
  )

  /**
   * Make an authenticated request
   * @param options Request options
   * @returns Promise with the response
   */
  const request = useCallback(
    async <TResponse>(
      options: AxiosRequestConfig,
    ): Promise<AxiosResponse<TResponse>> => {
      if (!axiosRequest[service]) {
        throw new Error(
          `Service "${service}" is not configured or does not exist`,
        )
      }

      const apiInstance = axiosRequest[service]

      const config = { ...options }
      const authHeaders = await getAuthHeaders()

      if (Object.keys(authHeaders).length > 0) {
        config.headers = {
          ...config.headers,
          ...authHeaders,
        }
      }

      return apiInstance(config)
    },
    [service, getAuthHeaders],
  )

  /**
   * Make an authenticated GraphQL query
   * @param query GraphQL query DocumentNode or string
   * @param variables Optional variables for the query
   * @param customHeaders Optional custom headers to include in the request
   * @returns Promise with the response
   */
  const gqlQuery = useCallback(
    async <TResponse>(
      query: DocumentNode | string,
      variables?: Record<string, unknown>,
      customHeaders?: Record<string, string>,
    ): Promise<AxiosResponse<TResponse>> => {
      if (!axiosRequest[service]) {
        throw new Error(
          `Service "${service}" is not configured or does not exist`,
        )
      }

      const apiInstance = axiosRequest[service]
      const GQLString = typeof query === 'string' ? query : print(query)
      const data = { query: GQLString, variables }
      const config: AxiosRequestConfig = {
        method: 'POST',
        data,
        headers: {
          ...customHeaders,
        },
      }

      const authHeaders = await getAuthHeaders({ includeBrand: true })

      if (Object.keys(authHeaders).length > 0) {
        config.headers = {
          ...config.headers,
          ...authHeaders,
        }
      }

      // Assuming the GraphQL endpoint is the base URL of the service.
      // If it's a specific path like /graphql, this would need adjustment
      // or the service URL itself should point to the GraphQL endpoint.
      return apiInstance(config)
    },
    [service, getAuthHeaders],
  )

  // Memoized so consumers can safely use these as hook dependencies. Returning
  // fresh function identities here re-triggers every effect that depends on a
  // service call, which is how PMHUB-31761 turned the Documents page into an
  // unbounded request loop.
  return useMemo(() => ({ request, query: gqlQuery }), [request, gqlQuery])
}

export default axiosRequest
